import React from 'react';
import type { DashboardPlatformPlayersData } from '../../../types';
import MetricStrip from './MetricStrip';
import TabSection from './TabSection';
import ChartCard from './charts/ChartCard';
import CompositionBarChart from './charts/CompositionBarChart';
import DonutChart from './charts/DonutChart';
import PlayerActivityChart from './charts/PlayerActivityChart';
import StackedBarChart from './charts/StackedBarChart';
import { BLUE_RAMP, CHART_COLORS } from './charts/common';
import {
  gatewayLabel,
  isCompositionEmpty,
  isSeriesEmpty,
  toBucketSeries,
  toPlayerSeries,
} from './executiveModel';

interface PlatformPlayersTabProps {
  data: DashboardPlatformPlayersData | undefined;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
}

// Opposing flows — separate stacks so the bars sit side by side and no
// meaningless deposits+withdrawals total is implied.
const FLOW_SERIES = [
  { dataKey: 'deposits', name: 'Deposits', color: BLUE_RAMP[1], stackId: 'in' },
  { dataKey: 'withdrawals', name: 'Withdrawals', color: CHART_COLORS.out, stackId: 'out' },
];

const FUNNEL_COLORS = [BLUE_RAMP[1], CHART_COLORS.risk];

/** Independent players — those with no shop attached (`users.shop_id IS NULL`). */
const PlatformPlayersTab: React.FC<PlatformPlayersTabProps> = ({
  data,
  isLoading,
  error,
  onRetry,
}) => {
  const providerDonut =
    data?.charts?.depositsByProvider?.map((p) => ({
      name: gatewayLabel(p.provider),
      value: Math.max(0, p.amount),
    })) ?? [];

  const funnel = data?.extras?.depositFunnel;
  const flow = toBucketSeries(data?.charts?.flow, ['deposits', 'withdrawals']);
  const players = toPlayerSeries(data?.charts);
  const bucketNote = data?.charts?.granularity === 'hour' ? 'Hourly buckets' : 'Daily buckets';

  const funnelMix = funnel
    ? [
        { name: 'Completed', value: Math.max(0, funnel.completed) },
        { name: 'Failed / expired / cancelled', value: Math.max(0, funnel.failed) },
      ]
    : [];

  return (
    <TabSection isLoading={isLoading} error={error} onRetry={onRetry}>
      {data && (
        <div className="space-y-4">
          <MetricStrip
            title="Money"
            columnsClassName="grid-cols-2 sm:grid-cols-4 xl:grid-cols-8"
            items={[
              { metric: 'depositsAmount', kpi: data.kpis.depositsAmount, format: 'currency', emphasis: true },
              { metric: 'depositsCount', kpi: data.kpis.depositsCount, format: 'number' },
              { metric: 'avgDeposit', kpi: data.kpis.avgDeposit, format: 'currency' },
              { metric: 'depositFailedRatePct', kpi: data.kpis.depositFailedRatePct, format: 'percent', invertDelta: true },
              { metric: 'withdrawalsAmount', kpi: data.kpis.withdrawalsAmount, format: 'currency' },
              { metric: 'withdrawalsCount', kpi: data.kpis.withdrawalsCount, format: 'number' },
              { metric: 'playersPendingWithdrawals', kpi: data.kpis.pendingWithdrawals, format: 'number' },
              { metric: 'pendingWithdrawalsAmount', kpi: data.kpis.pendingWithdrawalsAmount, format: 'currency' },
            ]}
          />

          <MetricStrip
            title="Players"
            columnsClassName="grid-cols-2 sm:grid-cols-4"
            items={[
              { metric: 'totalPlayers', kpi: data.kpis.totalPlayers, format: 'number' },
              { metric: 'activePlayers', kpi: data.kpis.activePlayers, format: 'number' },
              { metric: 'newPlayers', kpi: data.kpis.newPlayers, format: 'number' },
              { metric: 'playerCreditLiability', kpi: data.kpis.playerCreditLiability, format: 'currency' },
            ]}
          />

          <div className="grid gap-3 lg:grid-cols-2">
            <ChartCard
              metric="depositsVsWithdrawals"
              subtitle={bucketNote}
              isEmpty={isSeriesEmpty(flow, ['deposits', 'withdrawals'])}
            >
              <StackedBarChart
                data={flow}
                series={FLOW_SERIES}
                xKey="label"
                labelFormatter={(v) => v}
              />
            </ChartCard>

            <ChartCard
              metric="playerActivity"
              subtitle={bucketNote}
              isEmpty={isSeriesEmpty(players, ['newPlayers', 'activePlayers'])}
              emptyMessage="No player activity in this period"
            >
              <PlayerActivityChart data={players} />
            </ChartCard>
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <ChartCard
              metric="playerGrowth"
              subtitle={bucketNote}
              className="lg:col-span-2"
              isEmpty={isSeriesEmpty(players, ['totalPlayers'])}
              emptyMessage="No registered players yet"
            >
              <PlayerActivityChart data={players} variant="growth" />
            </ChartCard>

            <ChartCard metric="depositsByProvider" isEmpty={providerDonut.length === 0}>
              <DonutChart data={providerDonut} />
            </ChartCard>
          </div>

          <ChartCard
            metric="depositFunnel"
            isEmpty={isCompositionEmpty(funnelMix)}
            emptyMessage="No deposit attempts in this period"
          >
            <CompositionBarChart
              data={funnelMix}
              totalLabel="Deposit attempts"
              colors={FUNNEL_COLORS}
              valueFormatter={(v) => `${Math.round(v).toLocaleString('en-US')} attempts`}
            />
          </ChartCard>
        </div>
      )}
    </TabSection>
  );
};

export default PlatformPlayersTab;
